import { createFileRoute, Link } from "@tanstack/react-router";
import { useParams } from "@tanstack/react-router";
import { usePatient, usePatientTimeline } from "@/hooks/api/use-api";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { AlertTriangle, ArrowLeft, Calendar, Activity } from "lucide-react";

export const Route = createFileRoute("/patients/$patientId/")({
  component: PatientDetail,
});

function PatientDetail() {
  const { patientId } = useParams({ from: "/patients/$patientId" });
  const { data: p, isLoading, error } = usePatient(patientId);
  const { data: tl } = usePatientTimeline(patientId);

  if (isLoading) {
    return (
      <div className="space-y-4 p-6">
        <Skeleton className="h-12 w-64" />
        <Skeleton className="h-32 w-full" />
        <Skeleton className="h-32 w-full" />
      </div>
    );
  }

  if (error || !p) {
    return (
      <div className="p-6">
        <Card>
          <CardContent className="p-6 text-sm text-destructive">Patient introuvable</CardContent>
        </Card>
      </div>
    );
  }

  const age = p.birthDate ? Math.floor((Date.now() - new Date(p.birthDate).getTime()) / (365.25 * 24 * 3600 * 1000)) : "?";

  return (
    <div className="space-y-4 p-6">
      <Link to="/patients" className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground">
        <ArrowLeft className="h-3 w-3" /> Retour à la liste
      </Link>

      <Card>
        <CardHeader>
          <div className="flex items-start justify-between">
            <div>
              <CardTitle className="text-2xl">{p.family} {p.given}</CardTitle>
              <CardDescription>
                {p.mrn} · {p.gender} · {age} ans · grp {p.bloodGroup ?? "?"}
              </CardDescription>
            </div>
            {p.nationalId && <Badge variant="outline">CIN {p.nationalId}</Badge>}
          </div>
        </CardHeader>
      </Card>

      {/* Allergies — red banner if any */}
      {(p as any).allergies?.length > 0 && (
        <Card className="border-destructive/40 bg-destructive/10">
          <CardContent className="flex items-center gap-3 p-4">
            <AlertTriangle className="h-5 w-5 text-destructive" />
            <div>
              <div className="font-semibold text-destructive">Allergies connues</div>
              <div className="text-sm text-muted-foreground">
                {(p as any).allergies.map((a: any) => `${a.substance} (${a.severity})`).join(", ")}
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      <div className="grid gap-4 md:grid-cols-2">
        <Card>
          <CardHeader>
            <CardTitle>Coordonnées</CardTitle>
          </CardHeader>
          <CardContent className="space-y-2 text-sm">
            {p.phone && <div>📞 {p.phone}</div>}
            {p.email && <div>✉️ {p.email}</div>}
            {p.nationality && <div>🌍 {p.nationality}</div>}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Assurances actives</CardTitle>
          </CardHeader>
          <CardContent className="space-y-2 text-sm">
            {(p as any).insurances?.length > 0 ? (
              (p as any).insurances.map((i: any) => (
                <div key={i.id} className="flex items-center justify-between">
                  <span className="font-medium">{i.type}</span>
                  <Badge>{i.coveragePercent}%</Badge>
                </div>
              ))
            ) : (
              <div className="text-muted-foreground">Aucune assurance active</div>
            )}
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Activity className="h-4 w-4" />
            Timeline médicale unifiée
          </CardTitle>
          <CardDescription>Consultations, prescriptions, labos, imagerie, admissions — tout spécialité confondue</CardDescription>
        </CardHeader>
        <CardContent>
          {tl?.items.length ? (
            <ul className="space-y-2">
              {tl.items.slice(0, 20).map((e) => (
                <li key={e.id} className="flex items-start gap-3 rounded-md border border-white/5 bg-white/[0.02] p-3 text-sm">
                  <Calendar className="mt-0.5 h-3.5 w-3.5 text-muted-foreground" />
                  <div className="flex-1">
                    <div className="font-medium">{e.eventType}</div>
                    <div className="text-xs text-muted-foreground">
                      {new Date(e.occurredAt).toLocaleString("fr-FR")}
                    </div>
                  </div>
                </li>
              ))}
            </ul>
          ) : (
            <div className="text-sm text-muted-foreground">Aucun événement enregistré</div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
